import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SignInForm } from "../Components/SignInForm";
import { SignUpForm } from "../Components/SignUpForm";
import { ConfirmationForm } from "../Components/ConfirmationForm";
import { authService } from "../Services/Auth";

export default function AuthFlow() {
  const [email, setEmail] = useState("");
  const [authState, setAuthState] = useState<
    "initial" | "signUp" | "confirm" | "signIn"
  >("initial");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleContinue = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await authService.checkEmail(email);
      if (data.exists) {
        setAuthState(data.confirmed ? "signIn" : "confirm");
      } else {
        setAuthState("signUp");
      }
    } catch (error: any) {
      setError("An unexpected error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (formData: any) => {
    try {
      const data = await authService.signUp(formData);
      localStorage.setItem("token", data.token);
      setAuthState("confirm");
    } catch (error: any) {
      setError(error.response?.data?.error || error.message);
    }
  };

  const handleSignIn = async (email: string, password: string) => {
    try {
      const data = await authService.signIn(email, password);
      localStorage.setItem("token", data.token);
      localStorage.setItem("userEmail", email);
      navigate("/dashboard");
    } catch (error: any) {
      setError(error.response?.data?.error || error.message);
      console.error(error);
    }
  };

  const handleConfirmation = async (code: string) => {
    setError(null);
    try {
      await authService.verifyEmail(code, email);
      setAuthState("signIn");
    } catch (error: any) {
      setError(error.response?.data?.error || error.message);
    }
  };

  const handleResendCode = async () => {
    try {
      await authService.resendVerification(email);
    } catch (error: any) {
      setError(error.response?.data?.error || error.message);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-xl">
        <div className="bg-gray-800 shadow rounded-lg p-8 mt-36">
          {authState === "initial" && (
            <div>
              <h2 className="text-center text-2xl font-bold text-white mb-6">
                Sign in to your account
              </h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleContinue();
                }}
                className="mt-4 space-y-4"
              >
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-300 mb-1"
                  >
                    Email address
                  </label>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Checking...
                    </>
                  ) : (
                    "Continue"
                  )}
                </button>
              </form>
              {error && (
                <p className="mt-2 text-center text-red-400 text-sm">{error}</p>
              )}
            </div>
          )}
          {authState === "signUp" && (
            <SignUpForm initialEmail={email} onSubmit={handleSignUp} />
          )}
          {authState === "confirm" && (
            <ConfirmationForm
              email={email}
              onConfirm={handleConfirmation}
              onResendCode={handleResendCode}
              error={error}
            />
          )}
          {authState === "signIn" && (
            <SignInForm
              onSubmit={handleSignIn}
              initialEmail={email}
              error={error}
              onForgotPassword={() => navigate("/forgot-password")}
            />
          )}
        </div>
      </div>
    </div>
  );
}
